"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getScmReport = exports.initScmReport = exports.getReport = exports.initReport = exports.getAnalysis = exports.AnalysisStatus = exports.extendBundle = exports.checkBundle = exports.createBundle = exports.getFilters = exports.checkSession = exports.getIpFamily = exports.getVerifyCallbackUrl = exports.startSession = exports.compressAndEncode = void 0;
const uuid_1 = require("uuid");
const lodash_pick_1 = __importDefault(require("lodash.pick"));
const zlib_1 = require("zlib");
const util_1 = require("util");
const constants_1 = require("./constants");
const needle_1 = require("./needle");
const httpUtils_1 = require("./utils/httpUtils");
// The trick to typecast union type alias
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSubsetErrorCode(code, messages) {
    return code in messages;
}
function generateError(errorCode, messages, apiName, errorMessage, jsonApiError) {
    if (jsonApiError) {
        return httpUtils_1.generateErrorWithDetail(jsonApiError, errorCode, apiName);
    }
    if (!isSubsetErrorCode(errorCode, messages)) {
        throw { statusCode: errorCode, statusText: errorMessage || 'unknown error occurred', apiName };
    }
    const statusCode = errorCode;
    const statusText = errorMessage !== null && errorMessage !== void 0 ? errorMessage : messages[errorCode];
    return {
        type: 'error',
        error: {
            apiName,
            statusCode,
            statusText,
        },
    };
}
const GENERIC_ERROR_MESSAGES = {
    [constants_1.ErrorCodes.serverError]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.serverError],
    [constants_1.ErrorCodes.badGateway]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.badGateway],
    [constants_1.ErrorCodes.serviceUnavailable]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.serviceUnavailable],
    [constants_1.ErrorCodes.timeout]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.timeout],
    [constants_1.ErrorCodes.dnsNotFound]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.dnsNotFound],
    [constants_1.ErrorCodes.connectionRefused]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.connectionRefused],
};
async function compressAndEncode(payload) {
    // encode payload and compress;
    const deflate = util_1.promisify(zlib_1.gzip);
    return await deflate(Buffer.from(JSON.stringify(payload)).toString('base64'));
}
exports.compressAndEncode = compressAndEncode;
function startSession(options) {
    const { source, authHost } = options;
    const draftToken = uuid_1.v4();
    return {
        draftToken,
        loginURL: `${authHost}/login?token=${draftToken}&utm_medium=${source}&utm_source=${source}&utm_campaign=${source}&docker=false`,
    };
}
exports.startSession = startSession;
function getVerifyCallbackUrl(authHost) {
    return `${authHost}/api/verify/callback`;
}
exports.getVerifyCallbackUrl = getVerifyCallbackUrl;
/**
 * Dispatches a FORCED IPv6 request to test client's ISP and network capability.
 *
 * @return {number} IP family number used by the client.
 */
async function getIpFamily(authHost) {
    const family = 6;
    // Dispatch a FORCED IPv6 request to test client's ISP and network capability
    const res = await needle_1.makeRequest({
        url: getVerifyCallbackUrl(authHost),
        method: 'post',
        family, // family param forces the handler to dispatch a request using IP at "family" version
    }, 0);
    const ipv6Incompatible = res.error;
    return ipv6Incompatible ? undefined : family;
}
exports.getIpFamily = getIpFamily;
const CHECK_SESSION_ERROR_MESSAGES = {
    ...GENERIC_ERROR_MESSAGES,
    [constants_1.ErrorCodes.unauthorizedUser]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedUser],
    [constants_1.ErrorCodes.loginInProgress]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.loginInProgress],
};
async function checkSession(options) {
    const defaultValue = {
        type: 'success',
        value: '',
    };
    const res = await needle_1.makeRequest({
        url: getVerifyCallbackUrl(options.authHost),
        body: {
            token: options.draftToken,
        },
        family: options.ipFamily,
        method: 'post',
    });
    if (res.success) {
        return { ...defaultValue, value: (res.body.ok && res.body.api) || '' };
    }
    else if ([constants_1.ErrorCodes.loginInProgress, constants_1.ErrorCodes.badRequest, constants_1.ErrorCodes.unauthorizedUser].includes(res.errorCode)) {
        return defaultValue;
    }
    return generateError(res.errorCode, CHECK_SESSION_ERROR_MESSAGES, 'checkSession');
}
exports.checkSession = checkSession;
async function getFilters({ baseURL, orgId, attempts, source, extraHeaders, requestId, }) {
    const apiName = 'filters';
    let url;
    try {
        url = httpUtils_1.getURL(baseURL, '/' + apiName, orgId);
    }
    catch (err) {
        return generateError(400, err.message, apiName);
    }
    const res = await needle_1.makeRequest({
        headers: {
            source,
            ...extraHeaders,
            ...(requestId && { 'snyk-request-id': requestId }),
        },
        url,
        method: 'get',
    }, attempts);
    if (res.success) {
        return { type: 'success', value: res.body };
    }
    return generateError(res.errorCode, GENERIC_ERROR_MESSAGES, apiName, undefined, res.jsonApiError);
}
exports.getFilters = getFilters;
function commonHttpHeaders(options) {
    return {
        Authorization: options.sessionToken,
        source: options.source,
        ...(options.requestId && { 'snyk-request-id': options.requestId }),
        ...(options.org && { 'snyk-org-name': options.org }),
        ...options.extraHeaders,
    };
}
const CREATE_BUNDLE_ERROR_MESSAGES = {
    ...GENERIC_ERROR_MESSAGES,
    [constants_1.ErrorCodes.unauthorizedUser]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedUser],
    [constants_1.ErrorCodes.unauthorizedBundleAccess]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedBundleAccess],
    [constants_1.ErrorCodes.bigPayload]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.bigPayload],
    [constants_1.ErrorCodes.badRequest]: `Request payload doesn't match the specifications`,
    [constants_1.ErrorCodes.notFound]: 'Unable to resolve requested oid',
};
async function createBundle(options) {
    const payloadBody = await compressAndEncode(options.files);
    let url;
    try {
        url = httpUtils_1.getURL(options.baseURL, '/bundle', options.orgId);
    }
    catch (err) {
        return generateError(400, err.message, 'createBundle');
    }
    const payload = {
        headers: {
            'content-type': 'application/octet-stream',
            'content-encoding': 'gzip',
            ...commonHttpHeaders(options),
        },
        url,
        method: 'post',
        body: payloadBody,
        isJson: false,
    };
    const res = await needle_1.makeRequest(payload);
    if (res.success) {
        return { type: 'success', value: res.body };
    }
    return generateError(res.errorCode, CREATE_BUNDLE_ERROR_MESSAGES, 'createBundle', undefined, res.jsonApiError);
}
exports.createBundle = createBundle;
const CHECK_BUNDLE_ERROR_MESSAGES = {
    ...GENERIC_ERROR_MESSAGES,
    [constants_1.ErrorCodes.unauthorizedUser]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedUser],
    [constants_1.ErrorCodes.unauthorizedBundleAccess]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedBundleAccess],
    [constants_1.ErrorCodes.notFound]: 'Uploaded bundle has expired',
};
async function checkBundle(options) {
    let url;
    try {
        url = httpUtils_1.getURL(options.baseURL, `/bundle/${options.bundleHash}`, options.orgId);
    }
    catch (err) {
        return generateError(400, err.message, 'checkBundle');
    }
    const res = await needle_1.makeRequest({
        headers: {
            ...commonHttpHeaders(options),
        },
        url,
        method: 'get',
    });
    if (res.success)
        return { type: 'success', value: res.body };
    return generateError(res.errorCode, CHECK_BUNDLE_ERROR_MESSAGES, 'checkBundle', undefined, res.jsonApiError);
}
exports.checkBundle = checkBundle;
const EXTEND_BUNDLE_ERROR_MESSAGES = {
    ...GENERIC_ERROR_MESSAGES,
    [constants_1.ErrorCodes.unauthorizedUser]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedUser],
    [constants_1.ErrorCodes.bigPayload]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.bigPayload],
    [constants_1.ErrorCodes.badRequest]: `Bad request`,
    [constants_1.ErrorCodes.unauthorizedBundleAccess]: 'Unauthorized access to parent bundle',
    [constants_1.ErrorCodes.notFound]: 'Parent bundle has expired',
};
async function extendBundle(options) {
    const payloadBody = await compressAndEncode(lodash_pick_1.default(options, ['files', 'removedFiles']));
    let url;
    try {
        url = httpUtils_1.getURL(options.baseURL, `/bundle/${options.bundleHash}`, options.orgId);
    }
    catch (err) {
        return generateError(400, err.message, 'extendBundle');
    }
    const res = await needle_1.makeRequest({
        headers: {
            'content-type': 'application/octet-stream',
            'content-encoding': 'gzip',
            ...commonHttpHeaders(options),
        },
        url,
        method: 'put',
        body: payloadBody,
        isJson: false,
    });
    if (res.success)
        return { type: 'success', value: res.body };
    return generateError(res.errorCode, EXTEND_BUNDLE_ERROR_MESSAGES, 'extendBundle', undefined, res.jsonApiError);
}
exports.extendBundle = extendBundle;
// eslint-disable-next-line no-shadow
var AnalysisStatus;
(function (AnalysisStatus) {
    AnalysisStatus["waiting"] = "WAITING";
    AnalysisStatus["fetching"] = "FETCHING";
    AnalysisStatus["analyzing"] = "ANALYZING";
    AnalysisStatus["done"] = "DONE";
    AnalysisStatus["failed"] = "FAILED";
    AnalysisStatus["complete"] = "COMPLETE";
})(AnalysisStatus = exports.AnalysisStatus || (exports.AnalysisStatus = {}));
const GET_ANALYSIS_ERROR_MESSAGES = {
    ...GENERIC_ERROR_MESSAGES,
    [constants_1.ErrorCodes.unauthorizedUser]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedUser],
    [constants_1.ErrorCodes.unauthorizedBundleAccess]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedBundleAccess],
    [constants_1.ErrorCodes.notFound]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.notFound],
    [constants_1.ErrorCodes.badRequest]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.badRequest],
    [constants_1.ErrorCodes.serverError]: 'Getting analysis failed',
};
async function getAnalysis(options) {
    let url;
    try {
        url = httpUtils_1.getURL(options.baseURL, '/analysis', options.orgId);
    }
    catch (err) {
        return generateError(400, err.message, 'getAnalysis');
    }
    const config = {
        headers: {
            ...commonHttpHeaders(options),
        },
        url,
        method: 'post',
        body: {
            key: {
                type: 'file',
                hash: options.bundleHash,
                limitToFiles: options.limitToFiles || [],
                ...(options.shard ? { shard: options.shard } : null),
            },
            ...lodash_pick_1.default(options, ['severity', 'prioritized', 'legacy', 'analysisContext']),
        },
    };
    const res = await needle_1.makeRequest(config);
    if (res.success) {
        return { type: 'success', value: res.body };
    }
    return generateError(res.errorCode, GET_ANALYSIS_ERROR_MESSAGES, 'getAnalysis', undefined, res.jsonApiError);
}
exports.getAnalysis = getAnalysis;
const REPORT_ERROR_MESSAGES = {
    ...GENERIC_ERROR_MESSAGES,
    [constants_1.ErrorCodes.unauthorizedUser]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedUser],
    [constants_1.ErrorCodes.unauthorizedBundleAccess]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.unauthorizedBundleAccess],
    [constants_1.ErrorCodes.notFound]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.notFound],
    [constants_1.ErrorCodes.badRequest]: constants_1.DEFAULT_ERROR_MESSAGES[constants_1.ErrorCodes.badRequest],
    [constants_1.ErrorCodes.serverError]: 'Getting report failed',
};
/**
 * Trigger a file-based test with reporting.
 */
async function initReport(options) {
    let url;
    try {
        url = httpUtils_1.getURL(options.baseURL, `/report`, options.orgId);
    }
    catch (err) {
        return generateError(400, err.message, 'initReport');
    }
    const config = {
        headers: {
            ...commonHttpHeaders(options),
        },
        url,
        method: 'post',
        body: {
            workflowData: {
                projectName: options.report.projectName,
                targetName: options.report.targetName,
                targetRef: options.report.targetRef,
                remoteRepoUrl: options.report.remoteRepoUrl,
            },
            key: {
                type: 'file',
                hash: options.bundleHash,
                limitToFiles: options.limitToFiles || [],
                ...(options.shard ? { shard: options.shard } : null),
            },
            ...lodash_pick_1.default(options, ['severity', 'prioritized', 'legacy', 'analysisContext']),
        },
    };
    const res = await needle_1.makeRequest(config);
    if (res.success)
        return { type: 'success', value: res.body.reportId };
    return generateError(res.errorCode, REPORT_ERROR_MESSAGES, 'initReport', undefined, res.jsonApiError);
}
exports.initReport = initReport;
/**
 * Retrieve a file-based test with reporting.
 */
async function getReport(options) {
    var _a;
    let url;
    try {
        url = httpUtils_1.getURL(options.baseURL, `/report/${options.pollId}`, options.orgId);
    }
    catch (err) {
        return generateError(400, err.message, 'getReport');
    }
    const config = {
        headers: {
            ...commonHttpHeaders(options),
        },
        url,
        method: 'get',
    };
    const res = await needle_1.makeRequest(config);
    if (res.success)
        return { type: 'success', value: res.body };
    return generateError(res.errorCode, REPORT_ERROR_MESSAGES, 'getReport', (_a = res.error) === null || _a === void 0 ? void 0 : _a.message, res.jsonApiError);
}
exports.getReport = getReport;
/**
 * Trigger an SCM-based test with reporting.
 */
async function initScmReport(options) {
    let url;
    try {
        url = httpUtils_1.getURL(options.baseURL, `/test`, options.orgId);
    }
    catch (err) {
        return generateError(400, err.message, 'initReport');
    }
    const config = {
        headers: {
            ...commonHttpHeaders(options),
        },
        url,
        method: 'post',
        body: {
            workflowData: {
                projectId: options.projectId,
                commitHash: options.commitId,
            },
            ...lodash_pick_1.default(options, ['severity', 'prioritized', 'analysisContext']),
        },
    };
    const res = await needle_1.makeRequest(config);
    if (res.success)
        return { type: 'success', value: res.body.testId };
    return generateError(res.errorCode, REPORT_ERROR_MESSAGES, 'initReport', undefined, res.jsonApiError);
}
exports.initScmReport = initScmReport;
/**
 * Fetch an SCM-based test with reporting.
 */
async function getScmReport(options) {
    var _a;
    let url;
    try {
        url = httpUtils_1.getURL(options.baseURL, `/test/${options.pollId}`, options.orgId);
    }
    catch (err) {
        return generateError(400, err.message, 'getReport');
    }
    const config = {
        headers: {
            ...commonHttpHeaders(options),
        },
        url,
        method: 'get',
    };
    const res = await needle_1.makeRequest(config);
    if (res.success)
        return { type: 'success', value: res.body };
    return generateError(res.errorCode, REPORT_ERROR_MESSAGES, 'getReport', (_a = res.error) === null || _a === void 0 ? void 0 : _a.message, res.jsonApiError);
}
exports.getScmReport = getScmReport;
//# sourceMappingURL=http.js.map